import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import coinIcon from "../assets/coin.png";
import axios from "axios";
import "./AdminRewards.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { MdEdit, MdDelete } from "react-icons/md";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import openNotification from "components/utils/CustomNotification";
import Cropper from "react-easy-crop";

interface RewardItem {
  id: string;
  reward_uid: string;
  reward_type: "voucher" | "coupon";
  title: string;
  sub_title: string;
  expiry_date: string;
  active_status: boolean;
  image_details: {
    image_uid: string;
    image_url?: string;
  };
  reward_details: {
    terms_and_conditions: string;
    steps_to_redeem: string[];
    usage: string;
    discount?: string;
  };
}

const beAddress = process.env.REACT_APP_API_URL;

// Styled component for crop modal
const CropModalContainer = styled.div`
  position: fixed;
  inset: 0;
  background: black;
  z-index: 3000;
  display: flex;
  flex-direction: column;

  .crop-wrap {
    flex: 1;
    position: relative;
  }

  .controls {
    height: 100px;
    background: #222;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 20px;

    input[type="range"] {
      width: 80%;
      max-width: 400px;
    }

    .buttons {
      display: flex;
      gap: 16px;
      button {
        padding: 8px 24px;
        border-radius: 20px;
        border: none;
        background: white;
        font-weight: 600;
        cursor: pointer;
        &.cancel {
          background: #444;
          color: white;
        }
      }
    }
  }
`;

const AdminRewards = () => {
  useCheckSessionHeartBeat();
  const navigate = useNavigate();
  const [sessionUid, setSessionUid] = useState("");
  const [userUid, setUserUid] = useState("");
  const [activeTab, setActiveTab] = useState<"vouchers" | "coupons">(
    "vouchers",
  );
  const privilege = localStorage.getItem("privilege") ?? "";
  const userRole = localStorage.getItem("user_role") ?? "";
  const [selectedReward, setSelectedReward] = useState<any>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [earnedRewards, setEarnedRewards] = useState<any>({
    vouchers: [],
    coupons: [],
  });
  const [isEarnedSection, setIsEarnedSection] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingReward, setEditingReward] = useState<any>(null);
  const [deletingReward, setDeletingReward] = useState<any>(null);
  const [isedit, setIsedit] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [earnedStats, setEarnedStats] = useState({ vouchers: 0, coupons: 0 });

  // Image cropping states
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  interface NewReward {
    org_uid: string;
    reward_type: string;
    reward_name: string;
    image: string;
    description: string;
    platform: string;
    amount: number;
    currency: string;
    discount_type: string;
    dis_value: number;
    code: string;
    expiry_datetime: string;
    redeem_guide: string;
  }

  const initialRewardState: NewReward = {
    org_uid: "eminds",
    reward_type: "",
    reward_name: "",
    image: "",
    description: "",
    platform: "",
    amount: 0,
    currency: "INR",
    discount_type: "",
    dis_value: 0,
    code: "",
    expiry_datetime: "",
    redeem_guide: "",
  };

  const [newReward, setNewReward] = useState<NewReward>(initialRewardState);

  // API States
  const [data, setData] = useState<any>({
    vouchers: { todaysDeals: [], topDeals: [] },
    coupons: { todaysDeals: [], topDeals: [] },
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const [showCode, setShowCode] = useState(false);

  const fetchRewards = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${beAddress}/api/rewards`, {
        params: {
          reward_status: "unused",
          expired: "false",
        },
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const rewardsList: RewardItem[] = response.data?.data || [];

      const imageUids = rewardsList.map((r) => r.image_details.image_uid);
      const imageMap = await fetchImagesByUid(imageUids);

      const rewardsWithImages = rewardsList.map((r) => ({
        ...r,
        image_details: {
          ...r.image_details,
          image_url: imageMap[r.image_details.image_uid] || "",
        },
      }));

      const vouchers = rewardsWithImages.filter(
        (item: RewardItem) => item.reward_type === "voucher",
      );
      const coupons = rewardsWithImages.filter(
        (item: RewardItem) => item.reward_type === "coupon",
      );

      setData({
        vouchers: { todaysDeals: vouchers, topDeals: vouchers },
        coupons: { todaysDeals: coupons, topDeals: coupons },
      });
    } catch (err) {
      console.error(err);
      setError("Failed to load rewards.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnedRewards = async () => {
    try {
      const response = await axios.get(`${beAddress}/api/user-rewards`, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const responseData = response.data?.data;
      const userRewardsList = responseData?.rewards || [];

      setEarnedStats({
        vouchers: responseData?.total_voucher_earned || 0,
        coupons: responseData?.total_coupon_earned || 0,
      });

      if (userRewardsList.length === 0) {
        setEarnedRewards({ vouchers: [], coupons: [] });
        return;
      }

      const imageUids = userRewardsList
        .map((r: any) => r.image_details?.image_uid)
        .filter(Boolean);
      const imageMap = await fetchImagesByUid(imageUids);

      const processedRewards = userRewardsList.map((reward: any) => ({
        ...reward,
        title: reward.reward_name,
        image_details: {
          ...reward.image_details,
          image_url: imageMap[reward.image_details?.image_uid] || "",
        },
        code: reward.metadata?.code || "",
        reward_details: {
          usage: reward.metadata?.code,
          terms_and_conditions: "",
          steps_to_redeem: [reward.redeem_guide],
          discount_type: reward.metadata?.distribution_type,
          dis_value: reward.metadata?.value,
        },
      }));

      setEarnedRewards({
        vouchers: processedRewards.filter(
          (item: any) => item.reward_type === "voucher",
        ),
        coupons: processedRewards.filter(
          (item: any) => item.reward_type === "coupon",
        ),
      });
    } catch (err) {
      console.error("Failed to fetch earned rewards:", err);
      setEarnedRewards({ vouchers: [], coupons: [] });
    }
  };

  const fetchActivePoints = async () => {
    try {
      const response = await axios.get(`${beAddress}/api/redeem-points`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
      });

      if (response.data?.status === "Success") {
        setCoinBalance(response.data?.data?.active_points || 0);
      }
    } catch (err) {
      console.error("Failed to fetch active points:", err);
    }
  };

  const fetchImagesByUid = async (
    imageUids: string[],
  ): Promise<Record<string, string>> => {
    const limitedUids = imageUids.slice(0, 50);
    if (limitedUids.length === 0) return {};

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      limitedUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid),
      );
      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: { "user-uid": userUid },
        credentials: "include",
      });
      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => [
            item.image_uid,
            item.default ? "" : item.image,
          ]),
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }
    return {};
  };

  useEffect(() => {
    const s = localStorage.getItem("session_uid") || "";
    const u = localStorage.getItem("user_uid") || "";

    setSessionUid(s);
    setUserUid(u);
  }, []);

  useEffect(() => {
    if (!sessionUid || !userUid) return;
    fetchRewards();
    fetchEarnedRewards();
    fetchActivePoints();
  }, [sessionUid, userUid]);

  const availableRewards =
    activeTab === "vouchers"
      ? data?.vouchers?.todaysDeals || []
      : data?.coupons?.todaysDeals || [];

  const earnedRewardsList =
    activeTab === "vouchers" ? earnedRewards.vouchers : earnedRewards.coupons;

  const handleBuyClick = (reward: any, isEarned: boolean) => {
    setSelectedReward(reward);
    setIsEarnedSection(isEarned);
  };

  const handleCloseModal = () => setSelectedReward(null);

  const handleRewardChange = (e: any) => {
    const { name, value, type } = e.target;

    const noEmojiValue = value.replace(
      /[\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{2600}-\u{26FF}]/gu,
      "",
    );

    if (type === "number" && Number(noEmojiValue) < 0) return;

    if (name === "discount_type") {
      setNewReward((prev) => ({
        ...prev,
        [name]: value,
        dis_value: value === "fixed" ? prev.amount : prev.dis_value, // ✅
      }));
      return;
    }

    if (name === "dis_value" && newReward.discount_type === "percentage") {
      const numericValue = Number(noEmojiValue);
      if (noEmojiValue === "") {
        setNewReward((prev) => ({
          ...prev,
          [name]: noEmojiValue,
        }));
        return;
      }
      if (numericValue > 100) {
        setNewReward((prev) => ({
          ...prev,
          [name]: 100,
        }));
        return;
      }
    }

    setNewReward((prev) => {
      const updated = {
        ...prev,
        [name]: type === "number" ? Number(noEmojiValue) : noEmojiValue,
      };
      // Keep dis_value in sync with amount when discount_type is fixed
      if (name === "amount" && prev.discount_type === "fixed") {
        updated.dis_value = Number(noEmojiValue);
      }
      return updated;
    });
  };

  // Image upload handler with validation and crop modal
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // File size validation (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      openNotification({
        type: "error",
        message: "Image size exceeds 5MB limit. Please choose a smaller image.",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File type validation
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      openNotification({
        type: "error",
        message: "Please upload only JPG or PNG images.",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Read file and open crop modal
    const readFileAsDataURL = (inputFile: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("FileReader error"));
        reader.readAsDataURL(inputFile);
      });

    const loadImage = (src: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Image failed to load"));
        img.src = src;
      });

    try {
      const base64String = await readFileAsDataURL(file);
      const img = await loadImage(base64String);
      setTempImageForCrop(base64String);
      setIsCropModalOpen(true);
    } catch (err) {
      console.error("Image upload error", err);
      openNotification({ type: "error", message: "Failed to load image." });
    }
  };

  // Handle crop save
  const handleCropSave = async () => {
    if (!tempImageForCrop || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        tempImageForCrop,
        croppedAreaPixels,
      );

      // Set the cropped image
      setNewReward((prev) => ({ ...prev, image: croppedImage }));

      openNotification({
        type: "success",
        message: "Image cropped successfully!",
      });
    } catch (error) {
      console.error("Error saving crop", error);
      openNotification({
        type: "error",
        message: "An error occurred while cropping the image.",
      });
    }

    setIsCropModalOpen(false);
    setTempImageForCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Get cropped image
  const getCroppedImg = (imageSrc: string, pixelCrop: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No ctx");
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height,
        );
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      };
      image.onerror = reject;
    });
  };

  const handleSubmitReward = async () => {
    try {
      let payload;

      if (editingReward) {
        const changedValues: any = {};

        // Always include org_uid
        changedValues.org_uid = editingReward.org_uid || "eminds";

        if (newReward.reward_name !== editingReward.reward_name)
          changedValues.reward_name = newReward.reward_name;

        if (newReward.description !== editingReward.description)
          changedValues.description = newReward.description;

        if (newReward.platform !== editingReward.platform)
          changedValues.platform = newReward.platform;

        if (Number(newReward.amount) !== Number(editingReward.amount))
          changedValues.amount = newReward.amount;

        if (newReward.currency !== editingReward.currency)
          changedValues.currency = newReward.currency;

        if (
          newReward.code !==
          (editingReward.reward_details?.usage || editingReward.code)
        )
          changedValues.code = newReward.code;

        if (newReward.expiry_datetime !== editingReward.expiry_datetime)
          changedValues.expiry_datetime = newReward.expiry_datetime;

        if (newReward.redeem_guide !== editingReward.redeem_guide)
          changedValues.redeem_guide = newReward.redeem_guide;

        if (
          newReward.image &&
          newReward.image !== editingReward.image_details?.image_url
        )
          changedValues.image = newReward.image;

        // Only include discount fields if they actually changed
        if (newReward.reward_type === "coupon") {
          // const origDiscountType =
          //   editingReward.reward_details?.discount_type || "";
          // const origDisValue = Number(
          //   editingReward.reward_details?.dis_value || 0,
          // );
          const origDiscountType = editingReward.discount_type || "";
          const origDisValue = Number(editingReward.dis_value || 0);

          const newDisValue =
            newReward.discount_type === "fixed"
              ? Number(newReward.amount)
              : Number(newReward.dis_value);

          if (newReward.discount_type !== origDiscountType)
            changedValues.discount_type = newReward.discount_type;

          if (newDisValue !== origDisValue)
            changedValues.dis_value = newDisValue;
        }

        // Only 1 field changed = just org_uid, that means no real user changes
        if (Object.keys(changedValues).length === 1) {
          openNotification({ type: "info", message: "No changes detected" });
          return;
        }

        payload = [{ reward_uid: editingReward.reward_uid, ...changedValues }];
      } else {
        // For create mode, send all values
        payload = [newReward];
      }

      if (editingReward) {
        const response = await axios.put(`${beAddress}/api/rewards`, payload, {
          headers: {
            "user-uid": userUid,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const { status, message } = response.data;

        if (status === "Success") {
          openNotification({
            type: "success",
            message: message || "Reward updated successfully!",
          });

          setNewReward(initialRewardState);
          setEditingReward(null);
          fetchRewards();
          setShowCreateModal(false);
          setIsedit(false);
        } else {
          const errorMessage =
            response.data?.data || message || "Update reward failed.";
          openNotification({
            type: "error",
            message: errorMessage,
          });
        }
      } else {
        const response = await axios.post(`${beAddress}/api/rewards`, payload, {
          headers: {
            "user-uid": userUid,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const { status, message, data: responseData } = response.data;

        if (status === "Success") {
          openNotification({
            type: "success",
            message: message || "Reward created successfully!",
          });

          setNewReward(initialRewardState);
          setEditingReward(null);
          fetchRewards();
          setShowCreateModal(false);
          setIsedit(false);
        } else {
          const errorMessage =
            responseData || message || "Create reward failed.";
          openNotification({
            type: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error: any) {
      let errorMsg = editingReward
        ? "Failed to update reward."
        : "Failed to create reward.";
      if (error?.response?.data) {
        const { message, data } = error.response.data;
        errorMsg = data || message || errorMsg;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      openNotification({
        type: "error",
        message: errorMsg,
      });
    }
  };

  // const handleEditReward = (reward: any, e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   setEditingReward(reward);
  //   setIsedit(true);
  //   const steps = reward.reward_details?.steps_to_redeem;

  //   setNewReward({
  //     org_uid: reward.org_uid || "eminds",
  //     reward_type: reward.reward_type,
  //     reward_name: reward.reward_name,
  //     image: reward.image_details?.image_url || "",
  //     description: reward.description,
  //     platform: reward.platform,
  //     amount: reward.amount,
  //     currency: reward.currency || "INR",
  //     discount_type: reward.reward_details?.discount_type || "",
  //     dis_value:
  //       reward.reward_details?.discount_type === "fixed"
  //         ? reward.amount
  //         : reward.reward_details?.dis_value || 0,
  //     code: reward.reward_details?.usage || reward.code || "",
  //     expiry_datetime: reward.expiry_datetime,
  //     redeem_guide: reward.redeem_guide,
  //   });
  //   setShowCreateModal(true);
  // };

  const handleEditReward = (reward: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingReward(reward);
    setIsedit(true);

    // discount_type and dis_value are TOP-LEVEL fields from the API
    const discountType = reward.discount_type || "";
    const disValue =
      discountType === "fixed"
        ? Number(reward.amount)
        : Number(reward.dis_value) || 0;

    setNewReward({
      org_uid: reward.org_uid || "eminds",
      reward_type: reward.reward_type,
      reward_name: reward.reward_name,
      image: reward.image_details?.image_url || "",
      description: reward.description || "",
      platform: reward.platform || "",
      amount: reward.amount,
      currency: reward.currency || "INR",
      discount_type: discountType,
      dis_value: disValue,
      code: reward.code || "", // also top-level in API!
      expiry_datetime: reward.expiry_datetime,
      redeem_guide: reward.redeem_guide || "",
    });

    setShowCreateModal(true);
  };
  const handleDeleteReward = async () => {
    try {
      const requestBody = [deletingReward.reward_uid];

      const response = await axios.delete(`${beAddress}/api/rewards`, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: requestBody,
      });

      const { status, message } = response.data;

      if (status === "Success") {
        openNotification({
          type: "success",
          message: message || "Reward deleted successfully!",
        });

        fetchRewards();
        setShowDeleteModal(false);
        setDeletingReward(null);
      } else {
        openNotification({
          type: "error",
          message: message || "Delete reward failed.",
        });
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete reward.";

      openNotification({
        type: "error",
        message: errorMsg,
      });
    }
  };

  const handleRedeemReward = async (rewardUid: string) => {
    try {
      const response = await axios.get(`${beAddress}/api/redeem-reward`, {
        params: {
          reward_uid: rewardUid,
        },
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const { status, message } = response.data;

      if (status === "Success") {
        openNotification({
          type: "success",
          message: message || "Reward redeemed successfully!",
        });

        await Promise.all([
          fetchRewards(),
          fetchEarnedRewards(),
          fetchActivePoints(),
        ]);

        handleCloseModal();
      } else {
        openNotification({
          type: "error",
          message: message || "Failed to redeem reward.",
        });
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to redeem reward. Please try again.";

      openNotification({
        type: "error",
        message: errorMsg,
      });
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  if (loading) return <div className="RewardsMessage">Loading rewards...</div>;
  if (error) return <div className="RewardsMessage">{error}</div>;
  if (!data) return <div className="RewardsMessage">No rewards found.</div>;

  return (
    <div className="rewards-container">
      {/* Top Balance Section */}
      <div className="coin-balance-section">
        <div className="coin-left">
          <img src={coinIcon} alt="Coin Icon" className="coin-icon" />
          <div className="coin-banner">
            <h2>{coinBalance}</h2>
            <p>Points Balance</p>
          </div>
        </div>

        <div className="coin-right">
          <p>
            Earned Vouchers: <strong>{earnedStats.vouchers}</strong>
          </p>
          <p>
            Earned Coupons: <strong>{earnedStats.coupons}</strong>
          </p>
        </div>
        {(privilege === "systemadmin" ||
          privilege === "orgadmin" ||
          userRole === "creator") && (
          <div className="create_reward_btn">
            <button
              className="earned-history-btn"
              onClick={() => setShowCreateModal(true)}
            >
              Create Reward
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="rewards-buttons">
        <button
          className={activeTab === "vouchers" ? "active" : ""}
          onClick={() => setActiveTab("vouchers")}
        >
          Vouchers
        </button>
        <div className="divider"></div>
        <button
          className={activeTab === "coupons" ? "active" : ""}
          onClick={() => setActiveTab("coupons")}
        >
          Coupons
        </button>
      </div>
      <hr className="bottom-line" />

      {/* Available Section */}
      <section className="deals-section">
        <h2 className="deals-title">
          {activeTab === "vouchers"
            ? "Available Vouchers"
            : "Available Coupons"}
        </h2>
        <div className="deals-container">
          {availableRewards.slice(0, 3).map((deal: any, index: number) => (
            <div
              key={deal.id || index}
              className={`deal-card ${deal.expired ? "expired-card" : ""} ${
                index % 3 === 0
                  ? "green-card"
                  : index % 3 === 1
                    ? "purple-card"
                    : "blue-card"
              }`}
              onClick={() => handleBuyClick(deal, false)}
              style={{ cursor: "pointer" }}
            >
              {(privilege === "systemadmin" ||
                privilege === "orgadmin" ||
                userRole === "creator") && (
                <>
                  <button
                    className="card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.nextElementSibling?.classList.toggle(
                        "show",
                      );
                    }}
                  >
                    ⋮
                  </button>
                  <div className="card-dropdown">
                    <button onClick={(e) => handleEditReward(deal, e)}>
                      <MdEdit /> Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingReward(deal);
                        setShowDeleteModal(true);
                        e.currentTarget.parentElement?.classList.remove("show");
                      }}
                    >
                      <MdDelete /> Delete
                    </button>
                  </div>
                </>
              )}
              <img
                src={
                  deal.image_details?.image_url
                    ? `data:image/png;base64,${deal.image_details.image_url}`
                    : coinIcon
                }
                alt={deal.title || deal.reward_name}
              />

              <p>{deal.title || deal.reward_name}</p>

              {deal.reward_type === "coupon" &&
                deal.reward_details?.discount_type === "percentage" && (
                  <div className="discount-badge">
                    {deal.reward_details.dis_value}% OFF
                  </div>
                )}

              <span>
                {activeTab === "vouchers"
                  ? "Voucher Price: ₹"
                  : "Coupon Price: ₹"}
                {deal.cost || deal.amount}
              </span>

              <button
                className="buy-btn"
                disabled={deal.expired || deal.redeemable === false}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!deal.expired && deal.redeemable !== false) {
                    handleBuyClick(deal, false);
                  }
                }}
              >
                {deal.expired
                  ? "Expired"
                  : deal.redeemable === false
                    ? "Buy"
                    : "Buy"}
              </button>
            </div>
          ))}

          {availableRewards.length > 3 && (
            <div
              className="view-more-card"
              onClick={() =>
                navigate(`/all-rewards?type=available&tab=${activeTab}`)
              }
            >
              <p>View More</p>
              <span>→</span>
            </div>
          )}
        </div>
      </section>

      {/* Earned Section */}
      <section className="deals-section" style={{ marginBottom: "30px" }}>
        <h2 className="deals-title">
          {activeTab === "vouchers" ? "Earned Vouchers" : "Earned Coupons"}
        </h2>
        <div className="deals-container">
          {earnedRewardsList.length > 0 ? (
            <>
              {earnedRewardsList.slice(0, 3).map((deal: any, index: number) => (
                <div
                  key={deal.assignment_uid || deal.id || index}
                  className={`deal-card ${
                    index % 3 === 0
                      ? "green-card"
                      : index % 3 === 1
                        ? "purple-card"
                        : "blue-card"
                  }`}
                  onClick={() => handleBuyClick(deal, true)}
                >
                  <img
                    src={
                      deal.image_details?.image_url
                        ? `data:image/png;base64,${deal.image_details.image_url}`
                        : coinIcon
                    }
                    alt={deal.title || deal.reward_name}
                  />
                  <p>{deal.title || deal.reward_name}</p>

                  {deal.reward_type === "coupon" &&
                    deal.reward_details?.discount_type === "percentage" && (
                      <div className="discount-badge">
                        {deal.reward_details.dis_value}% OFF
                      </div>
                    )}

                  <span>
                    {activeTab === "vouchers"
                      ? "Voucher Value: ₹"
                      : "Coupon Value: ₹"}
                    {deal.amount}
                  </span>
                  <button className="buy-btn">Redeem</button>
                </div>
              ))}

              {earnedRewardsList.length > 3 && (
                <div
                  className="view-more-card"
                  onClick={() =>
                    navigate(`/all-rewards?type=earned&tab=${activeTab}`)
                  }
                >
                  <p>View More</p>
                  <span>→</span>
                </div>
              )}
            </>
          ) : (
            <p style={{ color: "#666", fontSize: "14px" }}>
              No earned {activeTab} yet
            </p>
          )}
        </div>
      </section>

      {/* Modal */}
      {selectedReward && (
        <div className="voucher-modal-overlay" onClick={handleCloseModal}>
          <div
            className="voucher-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="voucher-close-btn" onClick={handleCloseModal}>
              ✕
            </button>

            <div className="voucher-header">
              <div className="voucher-brand-section">
                <div className="voucher-brand-logo">
                  <img
                    src={
                      selectedReward.image_details?.image_url
                        ? `data:image/png;base64,${selectedReward.image_details.image_url}`
                        : coinIcon
                    }
                    alt={selectedReward.title || selectedReward.reward_name}
                  />
                </div>
                <div className="voucher-brand-info">
                  <h3 className="voucher-title">{selectedReward.title}</h3>
                  <p className="voucher-reward-name">
                    {selectedReward.reward_name}
                  </p>
                  <h2 className="voucher-amount">₹{selectedReward.amount}</h2>
                </div>
              </div>
            </div>

            <div className="voucher-body">
              {selectedReward.expiry_datetime && (
                <div className="voucher-expiry">
                  <span className="voucher-expiry-icon">⏰</span>
                  <p className="voucher-expiry-text">
                    Valid till{" "}
                    {new Date(
                      selectedReward.expiry_datetime,
                    ).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              )}

              {selectedReward.description && (
                <div className="voucher-section">
                  <h4 className="section-title">
                    <span>📝</span> Offer Details
                  </h4>
                  <div className="section-content">
                    {selectedReward.description}
                  </div>
                </div>
              )}

              {selectedReward.redeem_guide && (
                <div className="voucher-section">
                  <h4 className="section-title">
                    <span>📋</span> How to Redeem
                  </h4>
                  <div className="section-content">
                    {selectedReward.redeem_guide}
                  </div>
                </div>
              )}

              {!showCode ? (
                <button
                  className="voucher-btn voucher-btn-primary voucher-btn-full"
                  disabled={
                    !isEarnedSection &&
                    (selectedReward.redeemable === false ||
                      selectedReward.expired)
                  }
                  onClick={async () => {
                    if (isEarnedSection) {
                      setShowCode(true);
                    } else if (
                      selectedReward.redeemable !== false &&
                      !selectedReward.expired
                    ) {
                      await handleRedeemReward(selectedReward.reward_uid);
                    }
                  }}
                >
                  {selectedReward.expired ? (
                    <>
                      <span>🚫</span> Expired
                    </>
                  ) : !isEarnedSection &&
                    selectedReward.redeemable === false ? (
                    <>
                      <span>🔒</span> Buy with{" "}
                      {selectedReward.points
                        ? parseFloat(Number(selectedReward.points).toFixed(2))
                        : 0}{" "}
                      Points
                    </>
                  ) : isEarnedSection ? (
                    <>
                      <span>🎉</span> Get Code
                    </>
                  ) : (
                    <>
                      {/* <span>🛒</span> Buy with {selectedReward.points || 100}{" "}
                      Points */}
                      <span>🛒</span> Buy with{" "}
                      {selectedReward.points
                        ? parseFloat(Number(selectedReward.points).toFixed(2))
                        : 0}{" "}
                      Points
                    </>
                  )}
                </button>
              ) : (
                <>
                  <div className="voucher-code-section">
                    <p className="code-label">Your Coupon Code</p>
                    <div className="code-display">
                      <div className="code-text">
                        {selectedReward.reward_details?.usage ||
                          selectedReward.code}
                      </div>
                      <button
                        className="copy-code-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedReward.reward_details?.usage ||
                              selectedReward.code,
                          );
                          const btn = document.querySelector(".copy-code-btn");
                          if (btn) {
                            const originalHTML = btn.innerHTML;
                            btn.classList.add("copied");
                            btn.innerHTML = "✓ Copied!";
                            setTimeout(() => {
                              btn.classList.remove("copied");
                              btn.innerHTML = originalHTML;
                            }, 2000);
                          }
                        }}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>

                  <div className="voucher-actions">
                    <button
                      className="voucher-btn voucher-btn-secondary"
                      onClick={() => setShowCode(false)}
                    >
                      ← Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <ModalOverlay
          onClick={() => {
            setShowCreateModal(false);
            setEditingReward(null);
            setNewReward(initialRewardState);
          }}
        >
          <ModalContent
            className="wide-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{editingReward ? "Edit Reward" : "Create New Reward"}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingReward(null);
                  setNewReward(initialRewardState);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-form-grid">
              <div className="form-group">
                <label>Organization</label>
                <select
                  name="org_uid"
                  value={newReward.org_uid}
                  onChange={handleRewardChange}
                  disabled={isedit}
                >
                  <option value="eminds">eminds</option>
                </select>
              </div>

              <div className="form-group">
                <label>Reward Type</label>
                <select
                  name="reward_type"
                  value={newReward.reward_type}
                  onChange={handleRewardChange}
                  disabled={isedit}
                >
                  <option value="">Select</option>
                  <option value="voucher">Voucher</option>
                  <option value="coupon">Coupon</option>
                </select>
              </div>

              <div className="form-group">
                <label>Platform</label>
                <input
                  type="text"
                  name="platform"
                  placeholder="e.g. Amazon"
                  value={newReward.platform}
                  onChange={handleRewardChange}
                  disabled={isedit}
                />
              </div>

              <div className="form-group">
                <label>Reward Name</label>
                <input
                  type="text"
                  name="reward_name"
                  placeholder="Enter reward name"
                  value={newReward.reward_name}
                  onChange={handleRewardChange}
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  placeholder="Enter description"
                  value={newReward.description}
                  onChange={handleRewardChange}
                />
              </div>

              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  name="amount"
                  placeholder="0"
                  value={newReward.amount === 0 ? "" : newReward.amount}
                  onChange={handleRewardChange}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={newReward.currency}
                  onChange={handleRewardChange}
                >
                  <option value="INR">INR</option>
                </select>
              </div>

              <div className="form-group">
                <label>Discount Type</label>
                <select
                  name="discount_type"
                  value={newReward.discount_type}
                  onChange={handleRewardChange}
                  disabled={newReward.reward_type !== "coupon"}
                >
                  <option value="">Select</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Discount Value</label>
                <input
                  type="number"
                  name="dis_value"
                  placeholder="0"
                  value={newReward.dis_value === 0 ? "" : newReward.dis_value}
                  onChange={handleRewardChange}
                  disabled={
                    newReward.reward_type !== "coupon" ||
                    newReward.discount_type === "fixed" ||
                    newReward.discount_type === ""
                  }
                  min="0"
                  max={newReward.discount_type === "percentage" ? "100" : ""}
                />
                {newReward.discount_type === "percentage" &&
                  Number(newReward.dis_value) > 100 && (
                    <span
                      className="error-message"
                      style={{
                        fontSize: "12px",
                        color: "#ff4d4f",
                        marginTop: "4px",
                      }}
                    >
                      Percentage cannot exceed 100%
                    </span>
                  )}
              </div>

              <div className="form-group">
                <label>Redeem Code</label>
                <input
                  type="text"
                  name="code"
                  placeholder="Unique code"
                  value={newReward.code}
                  onChange={handleRewardChange}
                />
              </div>

              <div className="form-group">
                <label>Expiry Date</label>
                <Flatpickr
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i",
                    minDate: "today",
                    time_24hr: true,
                  }}
                  value={newReward.expiry_datetime}
                  onChange={(date) => {
                    setNewReward((prev) => ({
                      ...prev,
                      expiry_datetime: date[0] ? date[0].toISOString() : "",
                    }));
                  }}
                  className="date-input"
                />
              </div>

              <div className="form-group full-width">
                <label>Redeem Instructions</label>
                <textarea
                  name="redeem_guide"
                  placeholder="Step 1... Step 2..."
                  value={newReward.redeem_guide}
                  onChange={handleRewardChange}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>
                  Reward Image{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    (Max 5MB, JPG/PNG only)
                  </span>
                </label>
                <div className="image-upload-container">
                  {newReward.image && (
                    <div className="image-preview">
                      <img
                        src={
                          newReward.image.startsWith("data:")
                            ? newReward.image
                            : `data:image/png;base64,${newReward.image}`
                        }
                        alt="Preview"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".jpg,.jpeg,.png"
                    onChange={handleImageUpload}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingReward(null);
                  setNewReward(initialRewardState);
                }}
              >
                Cancel
              </button>
              <button className="submit-btn" onClick={handleSubmitReward}>
                {editingReward ? "Update Reward" : "Create Reward"}
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <ModalOverlay onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-modal-icon">⚠️</div>
              <h3>Remove Reward?</h3>
              <p>
                Are you sure you want to remove this reward? This action cannot
                be undone.
              </p>

              <div className="delete-modal-actions">
                <button
                  className="delete-cancel-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingReward(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleDeleteReward}
                >
                  Remove
                </button>
              </div>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* IMAGE CROP MODAL */}
      {isCropModalOpen && tempImageForCrop && (
        <CropModalContainer>
          <div className="crop-wrap">
            <Cropper
              image={tempImageForCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
          <div className="controls">
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <div className="buttons">
              <button onClick={handleCropSave}>Save</button>
              <button
                className="cancel"
                onClick={() => {
                  setIsCropModalOpen(false);
                  setTempImageForCrop(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </CropModalContainer>
      )}
    </div>
  );
};

export default AdminRewards;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  background: white;
  width: 40%;
  max-width: 800px;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
  }

  .modal-header h2 {
    margin: 0;
    color: #094752;
  }
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
  }

  .modal-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-group.full-width {
    grid-column: span 2;
  }

  .form-group label {
    font-weight: 600;
    font-size: 14px;
    color: #444;
  }

  .form-group input,
  .form-group select,
  .form-group textarea,
  .date-input {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
  }

  .form-group textarea {
    resize: vertical;
  }

  .image-upload-container {
    display: flex;
    align-items: center;
    gap: 20px;
    border: 1px dashed #ccc;
    padding: 15px;
    border-radius: 8px;
  }

  .image-preview img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #ddd;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #eee;
  }

  .cancel-btn {
    background: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
    padding: 10px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }

  .submit-btn {
    background: #094752;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    margin-top: 0px;
  }

  .submit-btn:hover {
    background: #06323a;
  }
  .cancel-btn:hover {
    background: #e0e0e0;
  }
`;
